import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Collapse,
  Divider,
  HStack,
  Icon,
  Input,
  Skeleton,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { DeleteIcon, RepeatIcon } from "@chakra-ui/icons";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";
import { ColumnSyncJobProto, ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { Header } from "build-ts/metadata";
import { MetadataTypeEnum } from "build-ts/enums";
import {
  deleteScheduledJob,
  getScheduledJobRunsByJobId,
  listScheduledJobsByOwnerId,
  putScheduledJob,
  runScheduledJob,
} from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { useEditorShellTokens } from "utils/editorShellStyles";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { useUser } from "context/UserContext";
import CronBuilder from "components/analyst/CronBuilder";
import AnalysisRunsViewer from "components/analyst/AnalysisRunsViewer";

interface ColumnSyncJobComponentProps {
  kgColumnHeader: Header;
  compact?: boolean;
}

const DEFAULT_CRON = "0 0 6 ? * MON-FRI";

const ColumnSyncJobComponent: React.FC<ColumnSyncJobComponentProps> = ({
  kgColumnHeader,
  compact,
}) => {
  const t = useEditorShellTokens(compact ?? false);
  const { defaultTimeZone } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const [existingJob, setExistingJob] = useState<ScheduledJobProto | null>(null);
  const [jobName, setJobName] = useState("");
  const [cronExpression, setCronExpression] = useState(DEFAULT_CRON);
  const [isActive, setIsActive] = useState(false);
  const [lastSyncMs, setLastSyncMs] = useState<number | null>(null);


  const deleteJobDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const loadJob = useCallback(async () => {
    if (!kgColumnHeader.id) return;
    setIsLoading(true);
    const jobs = await listScheduledJobsByOwnerId(kgColumnHeader.id);
    if (jobs) {
      const syncJob = jobs.find(
        (j: ScheduledJobProto) => j.type === ScheduledJobType.COLUMN_SYNC_JOB
      );
      if (syncJob) {
        setExistingJob(syncJob);
        setJobName(syncJob.header?.name || "");
        setCronExpression(syncJob.cron_expr || DEFAULT_CRON);
        setIsActive(syncJob.is_active || false);
        if (syncJob.header?.id) {
          const runs = await getScheduledJobRunsByJobId(syncJob.header.id);
          if (runs && runs.length > 0) {
            const latest = runs.reduce((a: Header, b: Header) =>
              (b.created_date_ms ?? 0) > (a.created_date_ms ?? 0) ? b : a
            );
            if (latest.created_date_ms) setLastSyncMs(latest.created_date_ms);
          }
        }
      }
    }
    setIsLoading(false);
  }, [kgColumnHeader.id]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  const handleSave = async () => {
    if (!jobName.trim()) {
      showToast("Please enter a job name", "", "warning");
      return;
    }
    setIsSaving(true);
    const job = ScheduledJobProto.create({
      header: {
        id: existingJob?.header?.id || uuidv4(),
        name: jobName.trim(),
        type: MetadataTypeEnum.SCHEDULED_JOB,
        owner_id: kgColumnHeader.id,
      },
      type: ScheduledJobType.COLUMN_SYNC_JOB,
      cron_expr: cronExpression,
      is_active: isActive,
      column_sync_job: ColumnSyncJobProto.create({
        kg_column_header: kgColumnHeader,
      }),
    });
    const response = await putScheduledJob(job);
    setIsSaving(false);
    if (response) {
      showToast(existingJob ? "Job updated" : "Job created", "", "success");
      setExistingJob(response);
      setJobName(response.header?.name || "");
      setCronExpression(response.cron_expr || DEFAULT_CRON);
      setIsActive(response.is_active || false);
    } else {
      showToast(existingJob ? "Failed to update job" : "Failed to create job", "", "error");
    }
  };

  const handleDeleteJob = async () => {
    if (!existingJob?.header?.id) return;
    deleteJobDialog.onClose();
    const ok = await deleteScheduledJob(existingJob.header.id);
    if (ok) {
      showToast("Job deleted", "", "success");
      setExistingJob(null);
      setJobName("");
      setCronExpression(DEFAULT_CRON);
      setIsActive(false);
      setLastSyncMs(null);
    } else {
      showToast("Failed to delete job", "", "error");
    }
  };

  const handleManualRun = async () => {
    if (!existingJob?.header?.id) return;
    setIsRunning(true);
    await runScheduledJob(existingJob.header.id);
    showToast("Job triggered — check runs below for status", "", "success");
    setIsRunning(false);
  };

  return (
    <>
      {/* Section bar — same pattern as "Source columns" in EditKGColumnComponent */}
      <HStack
        bg="gray.50"
        px={t.sectionPx}
        py={t.sectionPy}
        justify="space-between"
        align="center"
        w="100%"
        cursor="pointer"
        userSelect="none"
        onClick={() => setIsExpanded((p) => !p)}
        _hover={{ bg: "gray.100" }}
        borderTopWidth="1px"
        borderBottomWidth="1px"
        borderColor="gray.100"
      >
        <HStack spacing={2} flex="1" minW={0} justify="flex-start">
          <Text
            fontWeight="600"
            fontSize={t.sectionTitleSize}
            color="gray.600"
            textAlign="left"
            letterSpacing="normal"
            mb={0}
          >
            Column Sync Job
          </Text>
          {!isExpanded && (
            lastSyncMs ? (
              <Text fontSize="xs" color="gray.400" mb={0} fontWeight="400">
                · Last synced{" "}
                {epochConverterWithTimeZone(lastSyncMs, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)}{" "}
                {defaultTimeZone.toUpperCase()}
              </Text>
            ) : existingJob ? (
              <Text fontSize="xs" color="gray.400" mb={0} fontWeight="400">
                · Never run
              </Text>
            ) : (
              <Text fontSize="xs" color="gray.400" mb={0} fontWeight="400">
                · Not configured
              </Text>
            )
          )}
        </HStack>
        <Icon as={isExpanded ? FiChevronUp : FiChevronDown} boxSize={t.iconBox} color="gray.400" />
      </HStack>

      <Collapse in={isExpanded} animateOpacity>
        <Box px={t.sectionPx} pt={2} pb={2} bg="white">
          <VStack spacing={2} align="stretch">
            {isLoading ? (
              <VStack spacing={2} align="stretch">
                <Skeleton height="28px" borderRadius="md" />
                <Skeleton height="50px" borderRadius="md" />
              </VStack>
            ) : (
              <>
                {/* Name + actions in one compact row */}
                <HStack spacing={2} align="center">
                  <Input
                    {...t.field}
                    size="sm"
                    flex={1}
                    placeholder="Job name"
                    value={jobName}
                    onChange={(e) => setJobName(e.target.value)}
                  />
                  {existingJob && (
                    <Tooltip label="Run immediately" placement="top">
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="gray"
                        leftIcon={<RepeatIcon />}
                        isLoading={isRunning}
                        onClick={handleManualRun}
                        flexShrink={0}
                      >
                        Run
                      </Button>
                    </Tooltip>
                  )}
                  {existingJob && (
                    <Tooltip label="Delete job" placement="top">
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        leftIcon={<DeleteIcon />}
                        onClick={deleteJobDialog.onOpen}
                        flexShrink={0}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    size="xs"
                    colorScheme="purple"
                    isLoading={isSaving}
                    onClick={handleSave}
                    flexShrink={0}
                  >
                    {existingJob ? "Save" : "Create"}
                  </Button>
                </HStack>

                {/* Schedule */}
                <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <CronBuilder
                    cronExpression={cronExpression}
                    setCronExpression={setCronExpression}
                    isActive={isActive}
                    setIsActive={setIsActive}
                    jobDetails={existingJob || undefined}
                    onSave={(cron) => setCronExpression(cron)}
                  />
                </Box>

                {/* Runs */}
                {existingJob?.header && (
                  <>
                    <Divider mb={0}/>
                    <AnalysisRunsViewer selectedJob={existingJob.header} hideAnalystControls />
                  </>
                )}
              </>
            )}
          </VStack>
        </Box>
      </Collapse>

      {/* Delete confirmation */}
      <AlertDialog
        isOpen={deleteJobDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteJobDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Column Sync Job
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <strong>{existingJob?.header?.name || "this job"}</strong>? All
              associated runs will also be removed.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteJobDialog.onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteJob} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ColumnSyncJobComponent;
